<template>
  <div class="blog-main">
    <base-card-list
      :items="articles.result"
      infinity-scroll
      @load-more="loadMore">
      <template #card="{ item }">
        <article-card :article="item" />
      </template>
    </base-card-list>

    <hidden-pagination
      v-if="paginationParams.lastPage > 1"
      :params="paginationParams" />

    <footer-seo-text
      v-if="seoText"
      :text="seoText"
      class="game-service__seo-text container" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import AppModel from 'Models/AppModel';
import GameModel from 'Models/GameModel';
import MutationType from 'Models/MutationTypeModel';
import FooterSeoText from 'Components/FooterSeoText.vue';
import seoMixin from 'Root/mixins/seoMixin';
import { setNotFoundState } from 'Root/helpers/tools';

import HiddenPagination from 'Root/pages/game/components/HiddenPagination.vue';
import BaseCardList from 'Components/ui/BaseCardList/BaseCardList.vue';
import ArticleCard from 'Root/pages/blog/components/ArticleCard.vue';

function setHeaderInfo(store) {
  if (store.state.blog.activeGame) {
    store.commit('blog/mutateEntity', {
      entity: 'headerInfo',
      data: {
        title: `${store.state.blog.activeGame.fullTitle} Articles`,
      },
    });
  } else {
    store.commit('blog/mutateEntity', {
      entity: 'headerInfo',
      data: {
        title: 'Welcome to our Blog',
      },
    });
  }
}

function getArticlesParams(store, route) {
  const params = {};

  if (route.params.gameNamespace) {
    params.with_blog_category_game_id = GameModel.ROUTE_TO_DATA[route.params.gameNamespace].id;
  }

  if (route.params.categorySlug) {
    params.with_blog_category_slug = route.params.categorySlug;
  }

  if (route.params.authorSlug && store.state.blog.author) {
    params.with_blog_author_id = store.state.blog.author.id;
  }

  params.sort_column = 'published_at';
  params.sort_direction = 'desc';

  return params;
}

const limit = 20;
let offset = 0;
let isRequestsStopped = false;

function getPopularArticles(store) {
  return axios.get(`${AppModel.API_URI}/blog_posts/list?limit=20&sort_column=views_count&sort_direction=asc`)
    .then(({ data }) => {
      store.commit('blog/mutateEntity', {
        entity: 'popularArticles',
        data: data.result,
      });
    });
}

function getArticles(store, route, currentPage, isSSR = false) {
  if (isRequestsStopped) {
    return false;
  }

  // изменять переменные на сервере нельзя, они создаются 1 раз
  if (!isSSR) {
    isRequestsStopped = true;
  }

  const page = isSSR ? +route.query.page || 1 : currentPage;
  const currentOffset = (page - 1) * limit;

  const params = {
    ...getArticlesParams(store, route),
    limit,
    offset: currentOffset,
  };

  return axios.get(`${AppModel.API_URI}/blog_posts/list`, {
    params,
  })
    .then(({ data }) => {
      store.commit('blog/mutateNestedEntity', {
        entity: 'articles',
        nestedEntity: 'result',
        data: data.result,
        type: params.offset > 0 ? MutationType.PUSH : null,
      });

      store.commit('blog/mutateNestedEntity', {
        entity: 'articles',
        nestedEntity: 'total',
        data: data.total,
      });

      // изменять переменные на сервере нельзя, они создаются 1 раз
      if (!isSSR) {
        offset += data.result.length;
        isRequestsStopped = data.result.length < limit;
      }

      const totalPages = Math.ceil(data.total / limit);
      if (page > totalPages) {
        setNotFoundState(store);
      }
    })
    .catch((e) => {
      console.error(e);
      if (!isSSR) {
        isRequestsStopped = true;
      }
    });
}

function getCategoryBySlug(store, slug) {
  const gameId = store.state.blog.activeGame?.id;
  return store.getters['blog/getterCategoryBySlug'](slug, gameId) || null;
}

function setActiveCategory(store, category) {
  store.commit('blog/mutateEntity', {
    entity: 'activeCategory',
    data: category,
  });
}

export default {
  name: 'BlogMain',

  components: {
    FooterSeoText,
    BaseCardList,
    ArticleCard,
    HiddenPagination,
  },

  mixins: [seoMixin],

  beforeRouteUpdate(to, from, next) {
    offset = 0;
    isRequestsStopped = false;
    this.setupData(this.$store, to);

    next();
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      isRequestsStopped = false;
      offset = from.name ? 0 : vm.$store.state.blog.articles.result.length;
      vm.setupData(vm.$store, to);
    });
  },

  props: {
    isSilentSeo: {
      type: Boolean,
      default: false,
    },
  },

  asyncData({ store, router, context }) {
    if (context._is404) {
      return Promise.resolve();
    }

    const route = router.currentRoute;
    setHeaderInfo(store);

    const categoriesPromise = store.dispatch('blog/getBlogCategories', { limit: -1 })
      .then(() => {
        if (route.params.categorySlug) {
          const category = getCategoryBySlug(store, route.params.categorySlug);

          if (!category) {
            setNotFoundState(store, context);
          }

          setActiveCategory(store, category);
        }
      });

    const articlesPromise = getArticles(store, route, undefined, true);

    const popularArticlesPromise = getPopularArticles(store);

    return Promise.all([categoriesPromise, articlesPromise, popularArticlesPromise]);
  },

  data() {
    return {
      currentPageState: 1,
    };
  },

  computed: {
    ...mapState('blog', ['activeGame', 'activeCategory', 'categories', 'articles']),

    seoText() {
      return this.activeCategory?.seo_meta_content;
    },

    paginationParams() {
      return {
        link: this.$route.path,
        currentPage: +this.$route.query.page || 1,
        lastPage: Math.ceil(this.articles.total / limit),
      };
    },
  },

  created() {
    if (typeof window !== 'undefined') {
      offset = 0;
      isRequestsStopped = false;
    }
  },

  seo() {
    if (this.isSilentSeo) {
      return null;
    }

    const urn = typeof window === 'undefined'
      ? this.$ssrContext.url
      : window.location.pathname;

    const path = urn.split('?')[0];
    const page = +this.$route.query.page || 1;

    const relNext = page < Math.ceil(this.articles.total / limit)
      ? `${AppModel.URL}${path}?page=${page + 1}`
      : null;

    let relPrev = null;
    if (page === 2) {
      relPrev = `${AppModel.URL}${path}`;
    } else if (page > 2) {
      relPrev = `${AppModel.URL}${path}?page=${page - 1}`;
    }

    return {
      title: this?.activeCategory?.seo_title || this?.activeCategory?.title,
      meta: {
        description: this?.activeCategory?.seo_meta_description || this?.activeCategory?.description,
      },
      relNext,
      relPrev,
    };
  },

  methods: {
    async setupData(store, route) {
      const { categories, popularArticles } = store.state.blog;

      if (!categories.result.length) {
        await this.$store.dispatch('blog/getBlogCategories', { limit: -1 });
      }

      if (!popularArticles.length) {
        getPopularArticles(store);
      }

      const category = getCategoryBySlug(store, route.params.categorySlug);
      setActiveCategory(store, category);

      this.setFilter();

      setHeaderInfo(store);

      if (offset === 0) {
        getArticles(store, route, 1);
      }
    },

    setFilter() {
      const filter = {
        BlogFilterGame: {
          component: 'BlogFilterGame',
        },
      };

      if (this.activeGame) {
        const gameCategories = this.categories.result.filter((category) => category.game_id === this.activeGame.id);

        if (gameCategories.length) {
          filter.BlogFilterCategory = {
            component: 'BlogFilterCategory',
            props: {
              categories: gameCategories,
            },
          };
        }
      }

      filter.BlogFilterSearch = {
        component: 'BlogFilterSearch',
      };

      this.$store.commit('blog/mutateEntity', { entity: 'filter', data: filter });
    },

    loadMore() {
      if (this.paginationParams.lastPage === +this.$route.query.page) {
        return;
      }

      const nextPage = +this.$route.query.page + this.currentPageState
              || Math.floor(this.$store.state.blog.articles.result.length / limit) + 1;

      this.currentPageState += 1;

      getArticles(this.$store, this.$route, nextPage);
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
